import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import MessagingInbox from '../pages/MessagingInbox';
import MessagingService from './MessagingService';
import newMessageSound from '../sounds/messaging-new-message.mp3';
import { getAuthData } from '../utils/authStorage';

const NOTIFICATION_REPEAT_COUNT = 3;

const playNotificationSound = async () => {
  for (let i = 0; i < NOTIFICATION_REPEAT_COUNT; i += 1) {
    const audio = new Audio(newMessageSound);
    audio.volume = 0.85;
    try {
      await audio.play();
      await new Promise((resolve) => {
        audio.onended = resolve;
        audio.onerror = resolve;
      });
    } catch (error) {
      break;
    }
  }
};

const getStoredRoles = () => {
  const normalize = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);
  try {
    const sessionRoles = JSON.parse(sessionStorage.getItem('roles') || '[]');
    const cachedRoles = getAuthData()?.roles || [];
    return Array.from(new Set([...normalize(sessionRoles), ...normalize(cachedRoles)]));
  } catch (error) {
    return normalize(getAuthData()?.roles || []);
  }
};

function GlobalMessagingWidget() {
  const location = useLocation();
  const [roles, setRoles] = useState(getStoredRoles);
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const unreadCountRef = useRef(null);
  const openRef = useRef(false);

  const canUseMessaging = useMemo(() => roles.includes('admin') || roles.includes('support'), [roles]);

  useEffect(() => {
    const refreshRoles = () => setRoles(getStoredRoles());
    window.addEventListener('storage', refreshRoles);
    window.addEventListener('pegasus:auth-updated', refreshRoles);
    return () => {
      window.removeEventListener('storage', refreshRoles);
      window.removeEventListener('pegasus:auth-updated', refreshRoles);
    };
  }, []);

  useEffect(() => {
    if (!canUseMessaging) {
      setUnreadCount(0);
      return undefined;
    }

    let mounted = true;
    const refreshUnread = async () => {
      try {
        const data = await MessagingService.fetchUnreadCount();
        const nextUnread = Number(data?.unread || 0);
        if (!mounted) return;
        if (!openRef.current && unreadCountRef.current !== null && nextUnread > unreadCountRef.current) {
          playNotificationSound();
        }
        unreadCountRef.current = nextUnread;
        setUnreadCount(nextUnread);
      } catch (error) {
        if (mounted) setUnreadCount(0);
      }
    };

    refreshUnread();
    const interval = window.setInterval(refreshUnread, 15000);
    window.addEventListener('pegasus:messaging-unread-refresh', refreshUnread);

    return () => {
      mounted = false;
      window.clearInterval(interval);
      window.removeEventListener('pegasus:messaging-unread-refresh', refreshUnread);
    };
  }, [canUseMessaging]);

  useEffect(() => {
    openRef.current = open;
    if (open) {
      window.dispatchEvent(new Event('pegasus:collapse-sidebar'));
      document.body.style.overflow = 'hidden';
    }
    return () => {
      if (open) document.body.style.overflow = '';
    };
  }, [open]);

  if (!canUseMessaging || location.pathname === '/messaging') return null;

  const modal = open ? createPortal(
    <div
      className="modal d-block pegasus-global-messaging-modal"
      tabIndex="-1"
      style={{
        alignItems: 'center',
        background: 'rgba(15,23,42,.62)',
        display: 'flex',
        inset: 0,
        justifyContent: 'center',
        padding: '8px',
        position: 'fixed',
        zIndex: 2147483000
      }}
    >
      <div
        className="modal-dialog modal-dialog-centered m-0 pegasus-global-messaging-dialog"
        style={{
          height: 'calc(100dvh - 16px)',
          maxWidth: 'none',
          width: 'calc(100vw - 16px)'
        }}
      >
        <div
          className="modal-content border-0 shadow-lg"
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
          }}
        >
          <div className="modal-header py-2">
            <div className="d-flex align-items-center gap-2">
              <i className="fa-solid fa-comments text-primary" />
              <strong>Messagerie</strong>
              {unreadCount > 0 && <span className="badge bg-danger">{unreadCount} non lu(s)</span>}
            </div>
            <button type="button" className="btn-close" onClick={() => setOpen(false)} aria-label="Fermer" />
          </div>
          <div
            className="modal-body p-3"
            style={{
              display: 'flex',
              flex: '1 1 auto',
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <MessagingInbox embedded />
          </div>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        type="button"
        className="btn btn-primary rounded-circle shadow-lg d-flex align-items-center justify-content-center position-fixed"
        onClick={() => setOpen(true)}
        title="Messagerie"
        aria-label="Ouvrir la messagerie"
        style={{
          bottom: 26,
          right: 26,
          width: 66,
          height: 66,
          zIndex: 4900
        }}
      >
        <i className="fa-solid fa-comments fs-4" />
        {unreadCount > 0 && (
          <span
            className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
            style={{ border: '2px solid #fff', minWidth: 26 }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {modal}
    </>
  );
}

export default GlobalMessagingWidget;
